import { useState, lazy, Suspense } from "react";
import { useNavigate, useSearchParams, useLocation } from "react-router-dom";
import { getSafeReturnUrl } from "./utils/returnUrl";
import "./login.css";
import { useSession } from './hooks/useSession';
import { useUser } from './contexts/UserContext';
import { clearNewItemPhotoCache } from './utils/newItemPhotoCache';
import Modal from 'react-modal';
import { QrCode } from 'lucide-react';

const QRScanner = lazy(() => import('./components/QRScanner'));

Modal.setAppElement('#root');

// 仕入先ログインAPI（supplier-login-api）
const API_BASE_URL = '/api3006';

const Login = () => {
  const [searchParams] = useSearchParams();
  const isSessionExpired = searchParams.get("reason") === "session_expired";

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [showScanner, setShowScanner] = useState(false);
  const [scanning, setScanning] = useState(false);
  const [autoLogging, setAutoLogging] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const returnUrl = getSafeReturnUrl(location.state?.from);
  const { startSession } = useSession();
  const { setUser } = useUser();

  const completeLogin = (data) => {
    clearNewItemPhotoCache();
    if (data.token) {
      localStorage.setItem('token', data.token);
    }

    const userData = {
      id: data.id,
      userName: data.user_name,
      auth_class: data.auth_class ?? 0,
      maker_cd: data.maker_cd,
      maker_short_name: data.maker_short_name ?? null,
      new_item_flg: Number(data.new_item_flg) === 1 ? 1 : 0,
    };

    localStorage.setItem('userInfo', JSON.stringify(userData));
    setUser(userData);
    startSession(userData);
    navigate(returnUrl, { replace: true });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (scanning || autoLogging) {
      return;
    }
    setError("");

    try {
      const loginResponse = await fetch(`${API_BASE_URL}/login`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Accept": "application/json"
        },
        body: JSON.stringify({ email, password })
      });

      const data = await loginResponse.json();

      if (!loginResponse.ok) {
        setError(data.message || "Login Failed");
        return;
      }

      completeLogin(data);
    } catch (err) {
      console.error("Error details:", err);
      setError("Server connection failed");
    }
  };

  const autoLoginWithEmail = async (emailValue, passwordValue) => {
    setError("");
    setAutoLogging(true);

    try {
      const loginResponse = await fetch(`${API_BASE_URL}/login`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Accept": "application/json"
        },
        body: JSON.stringify({ email: emailValue, password: passwordValue })
      });

      const data = await loginResponse.json();

      if (!loginResponse.ok) {
        setError(data.message || "Login Failed");
        setAutoLogging(false);
        return;
      }

      completeLogin(data);
    } catch (err) {
      console.error("Auto login error:", err);
      setError("Server connection failed");
      setAutoLogging(false);
    }
  };

  const autoLoginWithQr = async (maker_cd, user_name, passwordValue) => {
    setError("");
    setAutoLogging(true);

    try {
      const loginResponse = await fetch(`${API_BASE_URL}/login_qr`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Accept": "application/json"
        },
        body: JSON.stringify({ maker_cd, user_name, password: passwordValue })
      });

      const data = await loginResponse.json();

      if (!loginResponse.ok) {
        setError(data.message || "Login Failed");
        setAutoLogging(false);
        return;
      }

      completeLogin(data);
    } catch (err) {
      console.error("QR auto login error:", err);
      setError("Server connection failed");
      setAutoLogging(false);
    }
  };

  const handleScan = (data) => {
    if (!data) return;
    try {
      // 新形式: maker:{maker_cd};user:{user_name};pw:{暗号化パスワード}
      const newMatch = data.match(/maker:(\d+);user:([^;]+);pw:(.+)/);
      if (newMatch) {
        const maker_cd = Number(newMatch[1]);
        const user_name = newMatch[2];
        const encryptedPassword = newMatch[3];
        setShowScanner(false);
        setError("");
        setTimeout(() => {
          setScanning(false);
          autoLoginWithQr(maker_cd, user_name, encryptedPassword);
        }, 100);
        return;
      }

      // 旧形式: id:{email};pw:{password}
      const legacyMatch = data.match(/id:(.*);pw:(.*)/);
      if (legacyMatch) {
        const scannedEmail = legacyMatch[1];
        const scannedPassword = legacyMatch[2];
        setShowScanner(false);
        setEmail(scannedEmail);
        setPassword(scannedPassword);
        setError("");
        setTimeout(() => {
          setScanning(false);
          autoLoginWithEmail(scannedEmail, scannedPassword);
        }, 100);
        return;
      }

      setError("Invalid QR code format");
      setShowScanner(false);
      setTimeout(() => setScanning(false), 100);
    } catch (err) {
      console.error("QR scan handling error:", err);
      setError("QR code processing error occurred");
      setShowScanner(false);
      setTimeout(() => setScanning(false), 100);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleSubmit(e);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-100">
      <div className="w-full max-w-md p-8 bg-white rounded-lg shadow-md">
        <h1 className="text-2xl font-bold mb-6 text-center text-gray-800">Supplier Login</h1>
        {isSessionExpired && (
          <p className="text-amber-600 text-center mb-4 bg-amber-50 py-2 px-3 rounded">
            Session expired. Please log in again.
          </p>
        )}
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="relative w-full">
            <input
              type="email"
              placeholder="Email address"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              onKeyPress={handleKeyPress}
              className="w-full px-4 py-2 border rounded-md pr-12 focus:outline-none focus:ring-2 focus:ring-blue-500"
              required
            />
            <button
              type="button"
              onClick={() => {
                setShowScanner(true);
                setScanning(true);
                setError("");
              }}
              className="absolute inset-y-0 right-0 flex items-center px-3 text-blue-600 hover:text-blue-800"
              disabled={scanning || autoLogging}
              title="QR Code Scan"
            >
              <QrCode size={20} />
            </button>
          </div>
          <div>
            <input
              type="password"
              placeholder="Password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              onKeyPress={handleKeyPress}
              className="w-full px-4 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              required
            />
          </div>
          <button
            type="submit"
            className="w-full py-2 px-4 bg-blue-500 text-white rounded-md hover:bg-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-opacity-50 transition-colors duration-200"
            disabled={autoLogging}
          >
            {autoLogging ? "Auto Login..." : "Login"}
          </button>
        </form>

        {error && <p className="text-red-500 text-center mt-4">{error}</p>}

        <Modal
          isOpen={showScanner}
          onRequestClose={() => {
            setShowScanner(false);
            setScanning(false);
            setError("");
          }}
          ariaHideApp={false}
          contentLabel="QR Scanner Modal"
          className="bg-white p-4 rounded shadow-md w-[90%] max-w-md mx-auto mt-20"
          overlayClassName="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
        >
          <div className="text-center">
            <h3 className="text-lg font-semibold mb-4">QR Code Scan</h3>
            <Suspense fallback={<p>Loading QR Scanner...</p>}>
              <QRScanner onScanSuccess={handleScan} />
            </Suspense>
            <button
              onClick={() => {
                setShowScanner(false);
                setScanning(false);
                setError("");
              }}
              className="mt-4 px-4 py-2 bg-gray-500 text-white rounded hover:bg-gray-600 transition-colors"
            >
              Close
            </button>
          </div>
        </Modal>
      </div>
    </div>
  );
};

export default Login;
